import {EditEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {useGetEvent} from '@/features/agenda/queries/useGetEvent'
import dayjs from 'dayjs'
import {useMemo} from 'react'

export const useEventFormDefaultValues = ({eventId}: {eventId?: string}) => {
    const eventQuery = useGetEvent({
        eventId,
        responseType: 'extended',
        options: {
            enabled: !!eventId
        }
    })

    const event = useMemo(() => eventQuery.data, [eventQuery.data])

    const eventFormDefaultValues: EditEventFormValidationSchema | undefined = useMemo(() => {
        if (!event || event.type !== 'event' || !event.category) {
            return undefined
        }

        const mapConsultants = () =>
            event.eventConsultants?.map(ec => ({
                id: ec.userId,
                fullName: ec.user.fullName,
                specializationName: ec.user.specialization,
                specializationId: ec.user.specializationId,
                isExternalStructure: ec.user.isExternalStructure,
                accessType: ec.accessType
                    ? {
                          id: ec.accessType.id,
                          accessType: ec.accessType.accessType
                      }
                    : undefined
            })) ?? []

        const mapPatients = () =>
            event.eventPatients?.map(ep => ({
                id: ep.patientId,
                fullName: ep.patient.fullName,
                treatmentRequestId: ep.project?.treatmentRequestId || null,
                neuromuscularDiagnosisId: ep.patient.neuromuscularDiagnosisId,
                project: ep.project
                    ? {
                          id: ep.project.id,
                          code: ep.project.code,
                          startDate: ep.project.startDate,
                          status: ep.project.status,
                          expirationDate: ep.project.expirationDate,
                          type: ep.project.type,
                          treatmentRequestId: ep.project.treatmentRequestId,
                          isSuspended: ep.project.isSuspended,
                          sector: ep.project.sector
                      }
                    : null
            })) ?? []

        const formatDateToDefaultValues = () => {
            if (!event.date || !event.time) return undefined
            return {
                date: dayjs(event.date, 'YYYY-MM-DD').toDate(),
                time: dayjs(event.time, 'HH:mm:ss').format('HH:mm'),
                location: event.location || 'clinic'
            }
        }

        const mapTeamMembers = () =>
            event.eventTeams.map(et => ({
                id: et.treatmentRequestTeamId,
                group: et.member.group,
                user: {
                    id: et.member.userId,
                    fullName: et.member.fullName,
                    specializationName: et.member.specializationName,
                    specializationId: et.member.specializationId ?? ''
                }
            }))

        const dateValues = formatDateToDefaultValues()

        if (!dateValues) {
            return undefined
        }

        const baseValues = {
            id: event.id,
            type: event.type,
            note: event.note ?? undefined,
            substitute: event?.eventConsultants?.[0].substitute,
            remoteUrl: event.remoteUrl ?? undefined,
            consultants: mapConsultants(),
            patients: mapPatients(),
            status: event.status,
            isGroupTreatment: !!event.isGroupTreatment,
            date: dateValues,
            duration: event.duration?.toString() ?? null,
            isARecovery: event.isARecovery,
            missedAccess: event.missedAccess ?? undefined
        }

        switch (event.category) {
            case 'treatments':
                return {
                    ...baseValues,
                    category: 'treatments',
                    isRemote: event.isRemote || false,
                    isRepeated: !!(event.type === 'event' && event.frequency && event.dayOfWeek && event.position),
                    repetitionData:
                        event.type === 'event' && event.frequency && event.dayOfWeek && event.position
                            ? {
                                  frequency: event.frequency,
                                  position: event.position,
                                  dayOfWeek: event.dayOfWeek
                              }
                            : undefined
                }
            case 'team_meeting':
                return {
                    ...baseValues,
                    category: 'team_meeting',
                    eventTeamMembers: mapTeamMembers(),
                    isRemote: false
                }
            case 'evaluations':
            case 'specialist_visits':
            case 'project_management':
                return {
                    ...baseValues,
                    category: event.category,
                    isRemote: event.isRemote || false
                }
            default:
                return undefined
        }
    }, [event])

    return {
        defaultValues: eventFormDefaultValues,
        isLoading: eventQuery.isLoading,
        isFetching: eventQuery.isFetching,
        isPending: eventQuery.isPending,
        isError: eventQuery.isError,
        refetch: eventQuery.refetch
    }
}
